import { Check, ChevronsUpDown, Layers, Settings2 } from '@orgus/ui/icons';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { create } from 'zustand';
import { ALL_PROJECTS_WORKSPACE_ID } from '@orgus/protocol';
import type { Workspace } from '@orgus/protocol';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Skeleton,
  cn,
  toast,
} from '@orgus/ui';
import { useRegisterCommands } from '../../lib/commands.ts';
import { useOrgus } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { useTeams } from './model.ts';

const KEY = 'as.workspace';

function read(): string {
  try {
    return localStorage.getItem(KEY) || ALL_PROJECTS_WORKSPACE_ID;
  } catch {
    return ALL_PROJECTS_WORKSPACE_ID;
  }
}

/** The workspace this browser shows. Kept per browser, like the theme. */
export const useWorkspaceChoice = create<{ id: string; choose: (id: string) => void }>()((set) => ({
  id: read(),
  choose: (id) => {
    try {
      localStorage.setItem(KEY, id);
    } catch {
      // private mode: the choice lasts for this tab
    }
    set({ id });
  },
}));

/** The chosen workspace, falling back to All projects when it was deleted. */
export function useCurrentWorkspace(): Workspace | null {
  const { data } = useTeams();
  const id = useWorkspaceChoice((s) => s.id);
  return useMemo(() => data?.workspaces.find((w) => w.id === id) ?? data?.workspaces.find((w) => w.id === ALL_PROJECTS_WORKSPACE_ID) ?? null, [data, id]);
}

/** Keeps the store's scope equal to the chosen workspace's projects. */
function useScopeSync(ws: Workspace | null): void {
  const key = ws?.projectIds ? ws.projectIds.join('\u0000') : null;
  useEffect(() => {
    useOrgus.getState().setScope(ws?.projectIds ?? null);
    // key captures the project list; ws itself changes identity on every teams update.
  }, [key]);
}

/**
 * The organisation and workspace in the top bar. A workspace narrows every page to its
 * projects; machines stay shared. With one org it reads as the org name and the workspace.
 */
export function WorkspaceSwitcher() {
  const { status, data } = useTeams();
  const current = useCurrentWorkspace();
  const choose = useWorkspaceChoice((s) => s.choose);
  const canManage = useCan('admin');
  const navigate = useNavigate();
  useScopeSync(status === 'ready' ? current : null);

  useRegisterCommands(
    (data?.workspaces ?? []).map((w) => ({
      id: `workspace.switch:${w.id}`,
      title: `Switch to ${w.name}`,
      group: 'Workspaces',
      icon: Layers,
      keywords: ['workspace', 'scope', 'org'],
      enabled: w.id !== current?.id,
      perform: () => choose(w.id),
    })),
    [data?.workspaces, current?.id],
  );

  if (status === 'loading') return <Skeleton className="h-6 w-36 shrink-0" />;
  if (status !== 'ready' || !data || !current) return null;

  const orgs = data.orgs;
  const scoped = current.projectIds !== null;
  const pick = (w: Workspace) => {
    if (w.id === current.id) return;
    choose(w.id);
    toast(`Showing ${w.name}`, { description: w.projectIds ? `${w.projectIds.length} ${w.projectIds.length === 1 ? 'project' : 'projects'}. Machines stay shared.` : 'Every project on this hub.' });
  };

  const count = (w: Workspace) => (w.projectIds ? `${w.projectIds.length} ${w.projectIds.length === 1 ? 'project' : 'projects'}` : 'Every project');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex h-control-sm min-w-0 max-w-64 shrink-0 items-center gap-2 rounded-control px-2 text-ui text-fg-muted transition-colors duration-(--duration-fast) hover:bg-hover hover:text-fg data-[state=open]:bg-hover data-[state=open]:text-fg"
        >
          <span aria-hidden className="flex size-4 shrink-0 items-center justify-center rounded-control bg-active text-caption font-semibold leading-none text-fg-muted">
            {(orgs[0]?.name ?? 'O').slice(0, 1).toUpperCase()}
          </span>
          <span className="sr-only">Workspace: </span>
          <span className="hidden truncate text-fg-subtle xl:inline">{orgs[0]?.name}</span>
          <span aria-hidden className="hidden text-fg-subtle xl:inline">/</span>
          <span className={cn('truncate', scoped ? 'font-medium text-fg' : '')}>{current.name}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-fg-subtle" aria-hidden />
          <span className="sr-only">. Switch workspace.</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        {orgs.map((org) => (
          <DropdownMenuGroup key={org.id}>
            <DropdownMenuLabel className="flex items-center gap-2">
              <span aria-hidden className="flex size-5 shrink-0 items-center justify-center rounded-control bg-active text-caption font-semibold text-fg-muted">
                {org.name.slice(0, 1).toUpperCase()}
              </span>
              <span className="truncate text-ui font-medium text-fg">{org.name}</span>
            </DropdownMenuLabel>
            {data.workspaces
              .filter((w) => w.orgId === org.id)
              .map((w) => {
                const on = w.id === current.id;
                return (
                  <DropdownMenuItem key={w.id} onSelect={() => pick(w)} aria-current={on ? 'true' : undefined}>
                    <span className="flex min-w-0 flex-1 items-center gap-2">
                      <span className={cn('min-w-0 flex-1 truncate', on ? 'text-fg' : 'text-fg-muted')}>{w.name}</span>
                      <span className="shrink-0 text-caption text-fg-subtle num">{count(w)}</span>
                      <Check className={cn('size-3.5 shrink-0', on ? 'text-accent-text' : 'invisible')} aria-hidden />
                    </span>
                  </DropdownMenuItem>
                );
              })}
          </DropdownMenuGroup>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem icon={<Settings2 />} onSelect={() => navigate('/settings/teams')} className="text-fg-muted">
          {canManage ? 'Manage workspaces' : 'Teams and workspaces'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
